import React, { useCallback, useMemo, useState } from 'react';
import {
  ActionsBar,
  Button,
  Column,
  GenericConfirmationModal,
  IconButton_IconSizes,
  Row,
  Section,
  Text,
} from '@ydistri/ds';
import ScopeDefinition from './ScopeDefinition';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../store';
import { setDraggingInfo } from './scopeConfigurationSlice';
import { MdDelete, MdDragIndicator, MdEdit } from 'react-icons/md';
import { styled } from 'styled-components';
import { Reorder, useDragControls } from 'framer-motion';
import { useDeleteConfigurationRuleMutation } from './apiScopes';
import { useScope } from './useScope';
import ScopeMissingEntityView from './ScopeMissingEntityView';
import { useOpenScopeModal } from './useOpenScopeModal';
import { DragHandleBase } from '../../../components/global/DragHandleBase/DragHandleBase';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import { generateScopeTitle, ViewMode } from './scopeUtilsLib';
import { useStoresReport } from './useStoresReport';
import { ruleMark, ruleMessages } from './storesReportLib';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

const markColor = { error: '#ffd6dc', warning: '#ffecef' } as const;

export const DragHandle = styled(DragHandleBase)<{ $grabbing?: boolean }>`
  cursor: ${p => (p.$grabbing ? 'grabbing' : 'grab')};
`;

interface ExceptionDefinitionProps {
  scopeId: number;
}

const ExceptionDefinition: React.FC<ExceptionDefinitionProps> = ({ scopeId }) => {
  const dispatch = useAppDispatch();

  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();
  const scope = useScope(scopeId);
  const openModalHandler = useOpenScopeModal(scope);
  const { problems } = useStoresReport(templateOrCalculation);

  const [deleteConfigurationRuleMutation] = useDeleteConfigurationRuleMutation();

  const rearrangeMode = useSelector((state: ReduxState) => state.scope.rearrangeMode);
  const controls = useDragControls();
  const [grabbing, setGrabbing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const showConfirmationModal = useCallback(() => setShowConfirmation(true), []);
  const hideConfirmationModal = useCallback(() => setShowConfirmation(false), []);

  const deleteExceptionHandler = useCallback(() => {
    deleteConfigurationRuleMutation({
      templateId: templateOrCalculation.id,
      id: scopeId,
    });
    hideConfirmationModal();
  }, [deleteConfigurationRuleMutation, hideConfirmationModal, scopeId, templateOrCalculation.id]);

  const headerActions = useMemo(() => {
    if (!isConfigurationEditable) return null;
    return (
      <ActionsBar moveToRight={true} wrap={false}>
        <ScopeMissingEntityView
          scopeId={scopeId}
          viewMode={ViewMode.EXCEPTION}
          templateOrCalculation={templateOrCalculation}
        />
        <Button $colorVariant="ghost" onClick={openModalHandler} data-testid="edit_exception">
          <MdEdit size={IconButton_IconSizes.small} />
          Adjust scope
        </Button>
        <Button
          $colorVariant="ghostDanger"
          onClick={showConfirmationModal}
          data-testid="create_new_exception"
        >
          <MdDelete size={IconButton_IconSizes.small} />
        </Button>
      </ActionsBar>
    );
  }, [
    isConfigurationEditable,
    scopeId,
    templateOrCalculation,
    openModalHandler,
    showConfirmationModal,
  ]);

  const onDragStart = useCallback(() => {
    setGrabbing(true);
    dispatch(
      setDraggingInfo({
        scopeId,
        finished: false,
        startingPriority: scope?.priority,
        endingPriority: scope?.priority,
      }),
    );
  }, [dispatch, scopeId, scope?.priority]);

  const onDragEnd = useCallback(() => {
    setGrabbing(false);
    dispatch(setDraggingInfo({ finished: true }));
  }, [dispatch]);

  const onPointerDownHandler = useCallback(
    (e: PointerEvent | React.PointerEvent) => controls.start(e),
    [controls],
  );

  const exceptionName = useMemo(() => {
    if (!scope) return `Exception ${scopeId}`;
    return scope.name && scope.name !== '' ? scope.name : generateScopeTitle(scope);
  }, [scope, scopeId]);

  if (scope?.creating) {
    return null;
  }

  // The server's report marks the card - red for an Error, rose for a Warning - and the card says why
  // at its foot, in the server's words; a colour with no reason is a puzzle.
  const mark = ruleMark(problems, scopeId);

  return (
    <>
      <Reorder.Item
        as="div"
        value={scopeId}
        dragListener={false}
        dragControls={controls}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <Row $gap="0.5rem" $width="100%" $flexWrap="nowrap">
          {rearrangeMode && (
            <DragHandle $grabbing={grabbing} onPointerDown={onPointerDownHandler}>
              <MdDragIndicator size={20} />
            </DragHandle>
          )}
          <Section
            collapsible={true}
            open={!rearrangeMode}
            $contentMarginTop={false}
            header={exceptionName}
            headerSuffix={
              <Text $type="light" $size="small">
                #{scopeId}
              </Text>
            }
            headerActions={headerActions}
            $ratio={1}
            $backgroundColor={mark ? markColor[mark] : undefined}
          >
            <ScopeDefinition
              scopeId={scopeId}
              viewMode={ViewMode.EXCEPTION}
              disabled={!isConfigurationEditable}
              templateOrCalculation={templateOrCalculation}
            />
            {mark && (
              <Column $gap="0.25rem">
                {ruleMessages(problems, scopeId).map(message => (
                  <Text key={message} $type={mark === 'error' ? 'danger' : 'warning'}>
                    {message}
                  </Text>
                ))}
              </Column>
            )}
          </Section>
        </Row>
      </Reorder.Item>
      {showConfirmation && (
        <GenericConfirmationModal
          title="Delete exception"
          message={`You are about to delete "${exceptionName}"`}
          confirmationButtonLabel="Delete"
          onConfirmed={deleteExceptionHandler}
          onCanceled={hideConfirmationModal}
        />
      )}
    </>
  );
};

export default ExceptionDefinition;
